import { createFileRoute } from "@tanstack/react-router"
import { Trans, useTranslation } from "react-i18next"

import { Button, LinkButton } from "@/globalComponents/atoms/Button"
import Card from "@/globalComponents/atoms/Card"
import Heading from "@/globalComponents/atoms/Heading"
import Icon from "@/globalComponents/atoms/Icon"
import Text from "@/globalComponents/atoms/Text"
import Modal from "@/globalComponents/molecules/Modal"
import { cleanEmptyParams } from "@/utilities/etc"
import { fetchWelcomeToken } from "@/utilities/hooks/queries/shared"
import { useTheme } from "@/utilities/hooks/useTheme"
import useToggle from "@/utilities/hooks/useToggle"

export const Route = createFileRoute("/qr/$qrLookupID/welcome")({
  preload: false,
  validateSearch: (
    search: Record<string, unknown>
  ): { redirectFrom?: string } => {
    return cleanEmptyParams({
      redirectFrom: (search.redirectFrom as string) || "",
    })
  },
  beforeLoad: async ({ context, params }) => {
    await fetchWelcomeToken({ qrLookupID: params.qrLookupID })
    context.queryClient.clear()
  },
  component: RouteComponent,
})

function RouteComponent() {
  const { t } = useTranslation()
  const locationName =
    useTheme().scannedArea?.theme.locationInformation.locationName
  const [showValidationModal, toggleValidationModal] = useToggle()
  return (
    <>
      <Heading
        className="center"
        level={4}
        as={1}
        style={{ color: "var(--secondary-11)" }}
      >
        <Trans i18nKey="welcome.header" values={{ locationName }}>
          Welcome to <span>{locationName}</span> Parking
        </Trans>
      </Heading>
      <Card>
        <div>
          <Heading level={1} as={2} className="center">
            {t("welcome.subhead")}
          </Heading>
          <AeronauticStripe />
        </div>
        <LinkButton
          hue="secondary"
          variant="default"
          from={Route.fullPath}
          to="/qr/$qrLookupID/gated/register/look-up"
        >
          <Icon name="parking" slot="left" />
          {t("welcome.registerVehicle")}
        </LinkButton>
      </Card>
      <Text level={3} className="center">
        {t("welcome.speakToAmbassador")}{" "}
        <Button
          hue="primary"
          presentation="link"
          onPress={toggleValidationModal}
        >
          {t("welcome.specialValidationRequests")}
        </Button>
      </Text>
      <SpecialValidationModal
        isOpen={showValidationModal}
        onOpenChange={toggleValidationModal}
      />
    </>
  )
}

export function AeronauticStripe() {
  return (
    <div className="aeronautic-stripe">
      <div />
      <div />
      <div />
    </div>
  )
}

interface SpecialValidationModalProps {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
}

function SpecialValidationModal(props: SpecialValidationModalProps) {
  const { t } = useTranslation()
  return (
    <Modal {...props}>
      <Modal.Header>
        <Heading level={2} slot="title">
          {t("welcome.specialValidationModal.header")}
        </Heading>
      </Modal.Header>
      <Modal.Body>
        <Text>{t("welcome.specialValidationModal.body")}</Text>
      </Modal.Body>
      <Modal.Footer arrangement="horizontal">
        {({ close }) => {
          return (
            <Button hue="neutral" variant="default" onPress={close}>
              {t("common.close")}
            </Button>
          )
        }}
      </Modal.Footer>
    </Modal>
  )
}
